import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography, CircularProgress, Button, IconButton } from '@mui/material';
import { Transition } from 'Shared/Transition';
import { CloseIcon } from 'Shared/Icons';

const formatTime = (totalSeconds) => {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const FlowExecutionModal = ({ open, timeout, onStop, onClose }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (!open) {
            setElapsed(0);
            return undefined;
        }
        const start = Date.now();
        setElapsed(0);
        const intervalId = setInterval(() => {
            setElapsed(Math.floor((Date.now() - start) / 1000));
        }, 1000);
        return () => clearInterval(intervalId);
    }, [open]);

    const timeoutSeconds = Number(timeout) > 0 ? Number(timeout) : null;
    const exceeded = timeoutSeconds !== null && elapsed >= timeoutSeconds;
    const progress = timeoutSeconds ? Math.min(100, (elapsed / timeoutSeconds) * 100) : 0;

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            disableEscapeKeyDown
            sx={{
                '& .MuiDialog-paper': {
                    width: '420px',
                    maxWidth: '90vw',
                    margin: 0,
                    padding: '34px 36px 30px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    position: 'relative',
                },
            }}
        >
            {onClose && (
                <IconButton
                    onClick={onClose}
                    aria-label='close'
                    size='small'
                    sx={{ position: 'absolute', top: '10px', right: '10px', color: '#949DB0' }}
                >
                    <CloseIcon />
                </IconButton>
            )}

            <Box sx={{ marginBottom: '18px' }}>
                <CircularProgress size={44} sx={{ color: '#289BEF' }} />
            </Box>

            <Typography component='h2' sx={{ margin: '0 0 18px', fontSize: '20px', fontWeight: 500, color: '#3B465C' }}>
                Flow is running
            </Typography>

            {timeoutSeconds ? (
                <>
                    {exceeded ? (
                        <Typography component='span' sx={{ display: 'block', fontSize: '16px', fontWeight: 500, color: '#3B465C', marginBottom: '10px', fontVariantNumeric: 'tabular-nums' }}>
                            Saving execution data
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                            <Typography component='span' sx={{ fontSize: '13px', color: '#949DB0' }}>Elapsed</Typography>
                            <Typography component='span' sx={{ fontSize: '16px', fontWeight: 500, color: '#3B465C', fontVariantNumeric: 'tabular-nums' }}>
                                {formatTime(elapsed)} / {formatTime(timeoutSeconds)}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ height: '8px', background: '#e7edf4', borderRadius: '4px', overflow: 'hidden', marginBottom: '18px' }}>
                        <Box sx={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #289BEF, #1BBC6F)' }} />
                    </Box>
                </>
            ) : (
                <Typography component='span' sx={{ display: 'block', fontSize: '16px', fontWeight: 500, color: '#3B465C', marginBottom: '18px', fontVariantNumeric: 'tabular-nums' }}>
                    {formatTime(elapsed)}
                </Typography>
            )}

            <Typography component='p' sx={{ margin: 0, fontSize: '12px', lineHeight: 1.5, color: '#949DB0' }}>
                Please wait while the flow is being executed.
                <br />
                Execution will stop automatically when the timeout is reached.
            </Typography>

            {onStop && (
                <Button
                    onClick={() => {
                        onStop();
                        if (onClose) {
                            onClose();
                        }
                    }}
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ marginTop: '18px' }}
                >
                    Stop
                </Button>
            )}
        </Dialog>
    );
};

export default FlowExecutionModal;
